import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoChevronBack } from "react-icons/io5";

const ViewOrder = () => {
  const navigate = useNavigate();
  const { orderItemId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderAccepted, setOrderAccepted] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [timeLeftMs, setTimeLeftMs] = useState(0);
  const [acceptMsg, setAcceptMsg] = useState("");

  useEffect(() => {
    const vendorToken = localStorage.getItem("vendorToken") || "<vendor JWT token>";
    setLoading(true);
    setError(null);
    fetch(`${process.env.REACT_APP_API_URL}/api/vendors/order/product/${orderItemId}`, {
      headers: {
        Authorization: `Bearer ${vendorToken}`,
      },
    })
      .then(res => res.json())
      .then(data => {
        if (!data.success) throw new Error(data.message || "Failed to fetch order details");
        setOrder(data);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [orderItemId]);

  useEffect(() => {
    if (!order?.product?.orderStatus) return;
    const acceptedStatuses = [
      "Preparing For Delivery",
      "Placed For Delivery",
      "Delivering",
      "Delivered",
      "Confirmed Delivery",
    ];
    if (acceptedStatuses.includes(order.product.orderStatus)) {
      setOrderAccepted(true);
      const initialMs = order?.product?.countdownTimer?.remainingMs || 0;
      setTimeLeftMs(initialMs);
    }
  }, [order]);

  const handleAcceptOrder = async () => {
    if (!orderItemId || accepting || orderAccepted) return;
    setAccepting(true);
    try {
      const vendorToken = localStorage.getItem("vendorToken") || "<vendor JWT token>";
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/vendors/orders/items/${orderItemId}/accept`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${vendorToken}`,
        },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to accept order");
      }
      setOrderAccepted(true);
      setAcceptMsg("Order Accepted. Take to BUY iT Hub for confirmation");
      const initialMs = order?.product?.countdownTimer?.remainingMs || 0;
      setTimeLeftMs(initialMs);
    } catch (err) {
      // leave button active if accept fails
    } finally {
      setAccepting(false);
    }
  };

  useEffect(() => {
    if (!orderAccepted) return;
    if (!timeLeftMs) return;
    const timer = setInterval(() => {
      setTimeLeftMs((prev) => (prev > 1000 ? prev - 1000 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [orderAccepted, timeLeftMs]);

  useEffect(() => {
    if (!acceptMsg) return;
    const timer = setTimeout(() => setAcceptMsg(""), 2000);
    return () => clearTimeout(timer);
  }, [acceptMsg]);

  const formatTimeLeft = (ms) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100 text-gray-500">Loading order details...</div>;
  }
  if (error) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100 text-red-500">{error}</div>;
  }
  if (!order) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100 text-gray-500">Order not found.</div>;
  }

  // Payment logic
  const paymentType = order?.orderMeta?.paymentMethod || order?.product?.paymentType || "POD";
  const paymentStatus = order?.orderMeta?.paymentStatus || (paymentType === "POD" ? "Pending" : "Paid");

  return (
    <div className="min-h-screen bg-gray-100">
      {acceptMsg && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-[#19C463] text-white px-6 py-3 rounded-lg shadow-lg text-sm font-semibold animate-fadeInOut" style={{ minWidth: "220px", textAlign: "center", letterSpacing: "0.5px" }}>
          {acceptMsg}
        </div>
      )}
      <header className="fixed top-0 left-0 right-0 z-10 flex h-14 items-center border-b border-gray-200 bg-white px-4">
        <button onClick={() => navigate(-1)} className="mr-4">
          <IoChevronBack className="h-6 w-6 text-gray-900" />
        </button>
        <h1 className="flex-1 text-center text-base font-semibold text-gray-900">
          ORDER ID: <span>{order.orderMeta?.orderNumber || order.product?.orderNumber}</span>
        </h1>
        <div className="w-6" />
      </header>

      <main className="mx-auto max-w-lg px-4 pt-20 pb-28">
        {/* Product image */}
        <div className="mb-6 aspect-[4/3] w-full rounded-lg bg-gray-200 overflow-hidden">
          <img
            loading="lazy"
            src={order?.product?.productImages?.[0] || "/New%20Image.jpeg"}
            alt={order?.product?.productName || "Product"}
            className="h-full w-full object-cover"
          />
        </div>

        <section className="mb-6">
          <h2 className="mb-3 text-sm font-medium text-gray-900">Product Details</h2>
          <div className="space-y-3 rounded-lg bg-white p-4 shadow-sm">
            <div className="flex justify-between">
              <p className="text-sm text-gray-600 text-left">Product Name:</p>
              <p className="text-sm text-gray-900 text-right">
                <span>{order.product?.productName}</span>
              </p>
            </div>
            <div className="flex justify-between">
              <p className="text-sm text-gray-600 text-left">Product Description:</p>
              <p className="text-sm text-gray-900 text-right">
                <span>{order.product?.description || order.product?.productDescription}</span>
              </p>
            </div>
            <div className="flex justify-between">
              <p className="text-sm text-gray-600 text-left">Product Price:</p>
              <p className="text-sm font-bold text-gray-900 text-right">
                <span>₦{Number(order.orderMeta?.amount ?? order.product?.price ?? 0).toLocaleString("en-NG")}</span>
              </p>
            </div>
            <div className="flex justify-between">
              <p className="text-sm text-gray-600 text-left">Product Category:</p>
              <p className="text-sm text-gray-900 text-right">
                <span>{order.product?.category || order.product?.productCategory}</span>
              </p>
            </div>
          </div>
        </section>

        <section className="mb-6">
          <h2 className="mb-3 text-sm font-medium text-gray-900">Order Details</h2>
          <div className="space-y-3 rounded-lg bg-white p-4 shadow-sm">
            <div className="flex justify-between">
              <p className="text-sm text-gray-600 text-left">Order ID:</p>
              <p className="text-sm text-gray-900 text-right">
                <span>{order.orderMeta?.orderNumber || order.product?.orderNumber}</span>
              </p>
            </div>
            <div className="flex justify-between">
              <p className="text-sm text-gray-600 text-left">Order Quantity:</p>
              <p className="text-sm text-gray-900 text-right">
                <span>{order.orderMeta?.orderQuantity ?? order.product?.quantity}</span>
              </p>
            </div>
          </div>
        </section>

        <section className="mb-6">
          <h2 className="mb-3 text-sm font-medium text-gray-900">Buyer Details</h2>
          <div className="space-y-3 rounded-lg bg-white p-4 shadow-sm">
            <div className="flex justify-between">
              <p className="text-sm text-gray-600">Buyer Name:</p>
              <p className="text-sm text-gray-900">
                <span>{order.buyerInfo?.fullName}</span>
              </p>
            </div>
          </div>
        </section>

        <section className="mb-6">
          <h2 className="mb-3 text-sm font-medium text-gray-900">Payment Status</h2>
          <div className="space-y-3 rounded-lg bg-white p-4 shadow-sm">
            <div className="flex justify-between">
              <p className="text-sm text-gray-600">Payment Type:</p>
              <p className="text-sm text-gray-900">
                <span>{paymentType}</span>
              </p>
            </div>
            <div className="flex justify-between">
              <p className="text-sm text-gray-600">Payment Status:</p>
              <p className="text-sm text-gray-900">
                <span>{paymentStatus}</span>
              </p>
            </div>
          </div>
        </section>

        <section className="mb-6">
          <h2 className="mb-3 text-sm font-medium text-gray-900">Order Status</h2>
          <div className="space-y-3 rounded-lg bg-white p-4 shadow-sm">
            <div className="flex justify-between">
              <p className="text-sm text-gray-600">Order Status:</p>
              <p className="text-sm text-gray-900">
                <span>{order.product?.orderStatus}</span>
              </p>
            </div>
            <div className="flex justify-between">
              <p className="text-sm text-gray-600">Address Hub To Confirm Product:</p>
              <p className="text-right text-sm text-gray-900">
                <span>With the BUY iT Hub, Abraka Delta</span>
              </p>
            </div>
          </div>
        </section>

        <div className="space-y-3">
          <button
            onClick={handleAcceptOrder}
            disabled={orderAccepted || accepting}
            className={`w-full rounded-lg py-3.5 text-sm font-medium text-white transition-colors ${
              orderAccepted || accepting ? "bg-[#AAAAAA]" : "bg-[#19C463] hover:bg-[#14a352]"
            }`}
          >
            Accept Order
          </button>

          {orderAccepted && (
            <p className="text-sm font-medium text-[#000000]">
              Please bring the product to BUY iT hub so we can proceed with delivery{" "}
              <span className="text-[#19C463]">Time Left: {formatTimeLeft(timeLeftMs)}</span>
            </p>
          )}

          {!orderAccepted && (
            <button className="w-full rounded-lg bg-[#FE0000] py-3.5 text-sm font-medium text-white transition-colors hover:bg-[#e00000]">
              Decline Order
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

export default ViewOrder;


